import {signal} from '@angular/core';
import {DebugCard, DebugNode} from '../model/debug-card';

const MAX_DEPTH = 10;
const MAX_ARRAY = 100;
const MAX_STRING = 500;

export function collectPageObjects(page: unknown): DebugCard[] {
  if (page === null || typeof page !== 'object') return [];

  let keys: string[];
  try {
    keys = Object.keys(page as object);
  } catch {
    return [];
  }

  const dataCards: DebugCard[] = [];
  const scalarRows: DebugNode[] = [];
  const serviceRows: DebugNode[] = [];

  for (const key of keys) {
    const raw = readProp(page, key);
    const {value, isSignal} = unwrapSignal(raw);
    if (typeof value === 'function') continue;
    const label = isSignal ? `${key} ()` : key;
    if (isOpaqueInstance(value)) {
      serviceRows.push({field: label, depth: 1, value: `[${classNameOf(value)}]`});
    } else if (isContainer(value)) {
      dataCards.push({
        title: label,
        className: classNameOf(value),
        rows: safeTree(value),
        expanded: signal(false),
      });
    } else {
      scalarRows.push(scalarNode(label, value));
    }
  }

  const cards: DebugCard[] = [];
  if (scalarRows.length > 0) cards.push(rowsCard('(scalars)', scalarRows));
  cards.push(...dataCards);
  if (serviceRows.length > 0) cards.push(rowsCard('(services)', serviceRows));
  return cards;
}

function rowsCard(title: string, rows: DebugNode[]): DebugCard {
  return {title, className: '', rows, expanded: signal(true)};
}

function scalarNode(field: string, value: unknown): DebugNode {
  if (value === null) return {field, depth: 1, value: 'null'};
  if (value === undefined) return {field, depth: 1, value: 'undefined'};
  if (value instanceof Date) {
    return {
      field,
      depth: 1,
      value: isNaN(value.getTime()) ? '[Invalid Date]' : value.toISOString(),
    };
  }
  return {field, depth: 1, value: formatScalar(value)};
}

function isContainer(v: unknown): boolean {
  return v !== null && typeof v === 'object' && !(v instanceof Date);
}

function isOpaqueInstance(v: unknown): boolean {
  if (v === null || typeof v !== 'object' || Array.isArray(v) || v instanceof Date) return false;
  const ctor = (v as {constructor?: unknown}).constructor as
    {name?: string; ɵprov?: unknown; ɵfac?: unknown} | undefined;
  if (ctor && (ctor.ɵprov || ctor.ɵfac)) return true;
  if (ctor?.name && /Service$/.test(ctor.name)) return true;
  const o = v as {subscribe?: unknown; next?: unknown};
  if (typeof o.subscribe === 'function' && typeof o.next === 'function') return true;
  return false;
}

function safeTree(obj: unknown): DebugNode[] {
  return childrenOf(obj, 1, new WeakSet<object>());
}

export function filterCards(cards: DebugCard[], query: string): DebugCard[] {
  if (!query.trim()) return cards;
  const match = buildMatcher(query);
  const out: DebugCard[] = [];
  for (const c of cards) {
    const titleMatch = match(c.title) || match(c.className);
    const rows = titleMatch ? c.rows : filterNodes(c.rows, match);
    if (titleMatch || rows.length) {
      c.expanded.set(true);
      out.push({...c, rows});
    }
  }
  return out;
}

function buildMatcher(query: string): (text: string) => boolean {
  const q = query.trim().toLowerCase();
  if (!q) return () => true;
  const escaped = q.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  let re: RegExp | null = null;
  try {
    re = new RegExp(escaped, 'i');
  } catch {
    re = null;
  }
  return (text) => {
    const t = (text ?? '').toLowerCase();
    return re ? re.test(t) : t.includes(q);
  };
}

function filterNodes(nodes: DebugNode[], match: (t: string) => boolean): DebugNode[] {
  const out: DebugNode[] = [];
  for (const n of nodes) {
    const selfMatch =
      match(n.field) ||
      (n.value != null && match(n.value)) ||
      (n.className != null && match(n.className)) ||
      (n.summary != null && match(n.summary));
    if (n.children) {
      const kids = selfMatch ? n.children : filterNodes(n.children, match);
      if (selfMatch || kids.length) {
        n.expanded?.set(true);
        out.push({...n, children: kids});
      }
    } else if (selfMatch) {
      out.push(n);
    }
  }
  return out;
}

export function setExpansionLevel(nodes: DebugNode[], maxLevel: number): void {
  walkBranches(nodes, (n) => n.expanded!.set(n.depth <= maxLevel));
}

export function setAllExpanded(nodes: DebugNode[], expanded: boolean): void {
  walkBranches(nodes, (n) => n.expanded!.set(expanded));
}

function walkBranches(nodes: DebugNode[], fn: (n: DebugNode) => void): void {
  for (const n of nodes) {
    if (n.children) {
      fn(n);
      walkBranches(n.children, fn);
    }
  }
}

function childrenOf(value: unknown, depth: number, seen: WeakSet<object>): DebugNode[] {
  if (value === null || value === undefined) {
    return [{field: '(value)', depth, value: value === null ? 'null' : 'undefined'}];
  }
  if (typeof value !== 'object') {
    return [{field: '(value)', depth, value: formatScalar(value)}];
  }

  let keys: string[];
  try {
    keys = Object.keys(value as object);
  } catch (e) {
    return [{field: '(error)', depth, value: `[keys-threw: ${errMsg(e)}]`}];
  }
  if (keys.length === 0) {
    const isArr = Array.isArray(value);
    return [{field: isArr ? '(empty array)' : '(empty object)', depth, value: isArr ? '[]' : '{}'}];
  }

  const limited = keys.slice(0, MAX_ARRAY);
  const nodes = limited.map((k) => {
    let v: unknown;
    try {
      v = (value as Record<string, unknown>)[k];
    } catch (e) {
      return {field: k, depth, value: `[getter-threw: ${errMsg(e)}]`};
    }
    return nodeFor(k, v, depth, seen);
  });
  if (keys.length > MAX_ARRAY) {
    nodes.push({field: '…', depth, value: `+${keys.length - MAX_ARRAY} more`});
  }
  return nodes;
}

function nodeFor(rawField: string, raw: unknown, depth: number, seen: WeakSet<object>): DebugNode {
  const {value: v, isSignal} = unwrapSignal(raw);
  const field = isSignal ? `${rawField} ()` : rawField;

  if (v === null) return {field, depth, value: 'null'};
  if (v === undefined) return {field, depth, value: 'undefined'};

  const t = typeof v;
  if (t === 'function') {
    const fn = v as {name?: string};
    return {field, depth, value: `[function${fn.name ? ' ' + fn.name : ''}]`};
  }
  if (t !== 'object') return {field, depth, value: formatScalar(v)};
  if (v instanceof Date) {
    return {field, depth, value: isNaN(v.getTime()) ? '[Invalid Date]' : v.toISOString()};
  }
  if (isOpaqueInstance(v)) return {field, depth, value: `[${classNameOf(v)}]`};

  const obj = v as object;
  if (seen.has(obj)) return {field, depth, value: '[circular]'};
  if (depth >= MAX_DEPTH) return {field, depth, value: summarize(obj)};

  seen.add(obj);
  return {
    field,
    depth,
    children: childrenOf(obj, depth + 1, seen),
    className: classNameOf(obj),
    summary: summarize(obj),
    expanded: signal(false),
  };
}

function classNameOf(v: unknown): string {
  if (Array.isArray(v)) return `Array(${v.length})`;
  if (v !== null && typeof v === 'object') {
    const proto = Object.getPrototypeOf(v);
    if (proto === null) return 'Object';
    return (v.constructor && v.constructor.name) || 'Object';
  }
  return typeof v;
}

function summarize(obj: object): string {
  if (Array.isArray(obj)) return `[array(${obj.length})]`;
  const ctor = (obj.constructor && obj.constructor.name) || 'Object';
  let count: number;
  try {
    count = Object.keys(obj).length;
  } catch {
    count = -1;
  }
  return count >= 0 ? `${ctor}{${count} keys}` : `${ctor}{?}`;
}

function formatScalar(v: unknown): string {
  const t = typeof v;
  if (t === 'string') {
    const s = v as string;
    return s.length > MAX_STRING ? s.slice(0, MAX_STRING) + '…' : s;
  }
  if (t === 'number' || t === 'boolean' || t === 'bigint' || t === 'symbol')
    return String(v as any);
  try {
    return String(v);
  } catch {
    return '[unprintable]';
  }
}

function readProp(obj: unknown, key: string): unknown {
  try {
    return (obj as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

function unwrapSignal(v: unknown): {value: unknown; isSignal: boolean} {
  if (typeof v !== 'function') return {value: v, isSignal: false};
  try {
    const syms = Object.getOwnPropertySymbols(v);
    if (syms.some((s) => s.description === 'SIGNAL')) {
      return {value: (v as () => unknown)(), isSignal: true};
    }
  } catch {
    // treat as plain function
  }
  return {value: v, isSignal: false};
}

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  try {
    return String(e);
  } catch {
    return '?';
  }
}
